import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { fetchLoadingDataWith } from '@/lib/service';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3WithWebApplicationJsonLd } from '@/lib/jsonld';

const IgFollowerExportContent = dynamic(() => import('@/components/IgFollowerExport'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;

  const [localeRes, fetchDataRes, tdkRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    fetchLoadingDataWith('/ig-follower-export-tool', locale),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const jsonLd = page3WithWebApplicationJsonLd(
    {
      title: 'Ig Follower Export Tool',
      desc: tdkRes?.data?.description ?? tempData?.head_Hashtag_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    {
      name: 'Instagram Follower Export Tool – DolphinRadar',
      url: 'https://www.dolphinradar.com/ig-follower-export-tool',
      applicationCategory: 'Instagram Follower Export Tool',
      description:
        'Easily export Instagram followers or following from any public account. Download to CSV or Excel for social data management — fast, secure, and anonymous.',
      featureList: [
        'Export Instagram followers',
        'Download Instagram following list',
        'Instagram follower export tool',
      ],
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      fetchDataRes: fetchDataRes?.data ?? [],
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Hashtag_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Hashtag_Meta_Desc ?? null,
      jsonLd,
    },
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return useMemo(() => <IgFollowerExportContent data={props} />, [props]);
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
